import {
  eventShiftsSchema,
  hoursLabel,
  minuteLabel,
  SHIFT_ROLE_LABELS,
  shiftCandidateSchema,
  ZONE_COLORS,
  type AdminEvent,
  type EventShift,
  type EventShifts,
  type EventTask,
  type ShiftCandidate,
  type ShiftDay,
} from '@growup/site-shared';
import { Autocomplete, Button, Card, cx, Field, Input, Notice, Select } from '@growup/ui';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';

import { copiedLine, CopyShifts, DayTasks, taskOwner, TaskEditor } from './EventTasks';

// A mao: o dia da semana abreviado do Intl muda de uma versao do browser para outra.
const WEEKDAY = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** `sáb 07/11` para um dia `aaaa-mm-dd`. */
export function dayLabel(day: string): string {
  const weekday = WEEKDAY[new Date(`${day}T12:00:00Z`).getUTCDay()] ?? '';
  return `${weekday} ${day.slice(8, 10)}/${day.slice(5, 7)}`;
}

const range = (from: number, to: number) => Array.from({ length: to - from }, (_, i) => from + i);

/**
 * Turnos sobrepostos na mesma zona ficam em linhas diferentes: cada turno vai
 * para a primeira linha onde cabe.
 */
export function lanes(shifts: readonly EventShift[]): Map<string, number> {
  const ends: number[] = [];
  const out = new Map<string, number>();
  for (const s of [...shifts].sort((a, b) => a.startHour - b.startHour)) {
    let lane = ends.findIndex((end) => end <= s.startHour);
    if (lane < 0) lane = ends.length;
    ends[lane] = s.endHour;
    out.set(s.id, lane);
  }
  return out;
}

/** Onde fica um turno na grelha de um dia. */
export interface Place {
  zoneId: string;
  startHour: number;
  endHour: number;
}

/** Agarrar o meio move o turno; agarrar uma ponta muda o inicio ou o fim. */
export type DragMode = 'move' | 'start' | 'end';

/**
 * O sitio de um turno depois de o arrastar `delta` horas (e, a mover, para
 * outra zona), sempre dentro das horas do dia e com pelo menos uma hora.
 */
export function placeAfter(
  shift: Place,
  mode: DragMode,
  delta: number,
  zoneId: string,
  openHour: number,
  closeHour: number,
): Place {
  const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);
  if (mode === 'start') {
    return { ...shift, startHour: clamp(shift.startHour + delta, openHour, shift.endHour - 1) };
  }
  if (mode === 'end') {
    return { ...shift, endHour: clamp(shift.endHour + delta, shift.startHour + 1, closeHour) };
  }
  const length = shift.endHour - shift.startHour;
  const startHour = clamp(shift.startHour + delta, openHour, closeHour - length);
  return { zoneId, startHour, endHour: startHour + length };
}

const samePlace = (a: Place, b: Place) =>
  a.zoneId === b.zoneId && a.startHour === b.startHour && a.endHour === b.endHour;

/** O ultimo turno arrastado, para o "Desfazer". */
interface Moved {
  shiftId: string;
  from: Place;
  to: Place;
  warning: string | null;
}

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

function summaryLine(d: ShiftDay): string {
  const s = d.summary;
  return [
    `Voluntários do dia: ${String(s.volunteersConfirmed)} confirmados`,
    `${String(s.volunteersWithShift)} com turno`,
    `${String(s.unfilled)} por preencher`,
    `${String(s.overlaps)} ${s.overlaps === 1 ? 'sobreposição' : 'sobreposições'}`,
    s.tasksUncovered > 0
      ? `${String(s.tasksUncovered)} ${s.tasksUncovered === 1 ? 'tarefa' : 'tarefas'} sem ninguém`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

type Editing =
  { id: string } | { id: null; zoneId: string; day: string; startHour: number; endHour: number };

type EditingTask = { id: string } | { id: null; zoneId: string; day: string; atMinute: number };

/**
 * Turnos do evento (docs/eventos, RF-E22 a RF-E26): uma grelha por dia, da
 * abertura ao fecho, com uma linha por zona da planta. Arrastar numa linha (ou
 * carregar numa hora) cria um turno. As tarefas do evento sao marcas na linha
 * da zona, a hora certa. Um turno arrasta-se para outra zona ou hora, e as
 * pontas mudam o inicio e o fim (tambem com as setas); arrastar nao manda
 * email e o ultimo movimento desfaz-se. Os avisos nao bloqueiam. So o staff
 * com `events.manage` distribui; cada pessoa recebe um email.
 */
export function ShiftsView({ event, events }: { event: AdminEvent; events: AdminEvent[] }) {
  const base = `/api/site/events/${event.id}/shifts`;
  const [data, setData] = useState<EventShifts | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [view, setView] = useState<'grid' | 'people'>('grid');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [editingTask, setEditingTask] = useState<EditingTask | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [moved, setMoved] = useState<Moved | null>(null);

  const load = useCallback(() => {
    getJson(base, eventShiftsSchema)
      .then((d) => {
        setData(d);
        setDay((current) => current ?? d.days[0]?.day ?? null);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [base]);
  useEffect(load, [load]);

  /**
   * Muda a zona ou as horas de um turno arrastado (ou movido com as setas).
   * Mostra logo o turno no sitio novo e volta atras se a API recusar.
   */
  const move = useCallback(
    async (shift: EventShift, to: Place, undo = false) => {
      const from = { zoneId: shift.zoneId, startHour: shift.startHour, endHour: shift.endHour };
      if (samePlace(from, to)) return;
      const place = (d: EventShifts, at: Place): EventShifts => ({
        ...d,
        shifts: d.shifts.map((s) => (s.id === shift.id ? { ...s, ...at } : s)),
      });
      setError(null);
      setCopied(null);
      setData((d) => d && place(d, to));
      try {
        const d = eventShiftsSchema.parse(
          await send('PUT', `${base}/${shift.id}`, {
            zoneId: to.zoneId,
            day: shift.day,
            startHour: to.startHour,
            endHour: to.endHour,
            needed: shift.needed,
            note: shift.note,
            accountIds: shift.people.map((p) => p.accountId),
            notify: false,
          }),
        );
        setData(d);
        const twice = (d.shifts.find((s) => s.id === shift.id)?.people ?? [])
          .filter((p) => p.overlap)
          .map((p) => p.login);
        setMoved(
          undo
            ? null
            : {
                shiftId: shift.id,
                from,
                to,
                warning:
                  twice.length === 0
                    ? null
                    : `⚠ ${twice.join(', ')} ${
                        twice.length === 1 ? 'tem' : 'têm'
                      } outro turno à mesma hora.`,
              },
        );
      } catch (err) {
        setData((d) => d && place(d, from));
        setError(messageOf(err));
      }
    },
    [base],
  );

  const undo = useCallback(() => {
    const shift = moved && data?.shifts.find((s) => s.id === moved.shiftId);
    if (moved && shift) void move(shift, moved.from, true);
  }, [data, move, moved]);

  // Ctrl+Z (ou Cmd+Z) desfaz o ultimo movimento, fora das caixas de texto.
  const onGrid = view === 'grid' && editing === null && editingTask === null;
  useEffect(() => {
    if (!moved || !onGrid) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        if (isTyping(e.target)) return;
        e.preventDefault();
        undo();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moved, onGrid, undo]);

  if (data === null) {
    return error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>;
  }

  const current = data.days.find((d) => d.day === day) ?? data.days[0];
  if (!current) return null;

  if (editingTask !== null) {
    const task =
      editingTask.id === null ? null : (data.tasks.find((t) => t.id === editingTask.id) ?? null);
    return (
      <TaskEditor
        key={editingTask.id ?? 'new-task'}
        base={base}
        data={data}
        task={task}
        draft={editingTask.id === null ? editingTask : null}
        onSaved={(d) => {
          setData(d);
          setEditingTask(null);
        }}
        onClose={() => setEditingTask(null)}
      />
    );
  }

  if (editing !== null) {
    const shift =
      editing.id === null ? null : (data.shifts.find((s) => s.id === editing.id) ?? null);
    return (
      <ShiftEditor
        key={editing.id ?? 'new'}
        base={base}
        data={data}
        shift={shift}
        draft={editing.id === null ? editing : null}
        onSaved={(d) => {
          setData(d);
          setEditing(null);
        }}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <Card
      headingLevel={3}
      title="Turnos"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              view === 'grid'
                ? printDay(event.title, data, current)
                : printPeople(event.title, data)
            }
          >
            {view === 'grid' ? 'Imprimir o dia' : 'Imprimir a escala'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            aria-pressed={view === 'people'}
            onClick={() => setView((v) => (v === 'grid' ? 'people' : 'grid'))}
          >
            {view === 'grid' ? 'Escala por pessoa' : 'Grelha do dia'}
          </Button>
          {data.zones.length > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                setEditingTask({
                  id: null,
                  zoneId: data.zones[0]?.id ?? '',
                  day: current.day,
                  atMinute: current.openHour * 60,
                })
              }
            >
              Nova tarefa
            </Button>
          ) : null}
          {data.zones.length > 0 ? (
            <Button
              size="sm"
              onClick={() =>
                setEditing({
                  id: null,
                  zoneId: data.zones[0]?.id ?? '',
                  day: current.day,
                  startHour: current.openHour,
                  endHour: Math.min(current.openHour + 2, current.closeHour),
                })
              }
            >
              Novo turno
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {copied ? <Notice tone="success">{copied}</Notice> : null}
        {moved && view === 'grid' ? (
          <Notice tone={moved.warning ? 'warning' : 'success'}>
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>
                Turno movido para {data.zones.find((z) => z.id === moved.to.zoneId)?.name ?? '—'},{' '}
                {hoursLabel(moved.to.startHour, moved.to.endHour)}. Ninguém recebe email.
                {moved.warning ? ` ${moved.warning}` : ''}
              </span>
              <Button size="sm" variant="secondary" onClick={undo}>
                Desfazer (Ctrl+Z)
              </Button>
            </span>
          </Notice>
        ) : null}
        {data.shifts.length === 0 && data.tasks.length === 0 ? (
          <CopyShifts
            base={base}
            events={events.filter((e) => e.id !== event.id)}
            onCopied={(result) => {
              setData(result.shifts);
              setCopied(copiedLine(result));
            }}
          />
        ) : null}
        {view === 'people' ? (
          <PeopleView data={data} />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div role="group" aria-label="Dia" className="flex flex-wrap gap-2">
                {data.days.map((d) => (
                  <button
                    key={d.day}
                    type="button"
                    aria-pressed={d.day === current.day}
                    onClick={() => setDay(d.day)}
                    className={cx(
                      'border-2 px-3 py-1.5 font-sub text-label font-bold uppercase',
                      d.day === current.day
                        ? 'border-gu-accent text-gu-ink'
                        : 'border-gu-line text-gu-ink-muted hover:text-gu-ink',
                    )}
                  >
                    {dayLabel(d.day)} · {hoursLabel(d.openHour, d.closeHour)}
                  </button>
                ))}
              </div>
              <p className="text-caption text-gu-ink-muted">{summaryLine(current)}</p>
            </div>
            <DayHours
              key={`${current.day}-${String(current.openHour)}-${String(current.closeHour)}`}
              base={base}
              day={current}
              onSaved={setData}
            />
            {current.withoutShift.length > 0 ? (
              <Notice tone="warning" title="Voluntários confirmados sem turno">
                {current.withoutShift.map((p) => p.login).join(', ')}
              </Notice>
            ) : null}
            {data.zones.length === 0 ? (
              <Notice tone="info">
                Os turnos são por zona: crie primeiro as zonas no separador Espaço.
              </Notice>
            ) : (
              <Grid
                data={data}
                day={current}
                onOpen={(id) => setEditing({ id })}
                onOpenTask={(id) => setEditingTask({ id })}
                onCreate={(zoneId, startHour, endHour) =>
                  setEditing({ id: null, zoneId, day: current.day, startHour, endHour })
                }
                onMove={(shift, to) => void move(shift, to)}
              />
            )}
            <DayTasks
              base={base}
              data={data}
              day={current}
              onOpen={(id) => setEditingTask({ id })}
              onSaved={setData}
            />
            <p id="shift-keys" className="text-caption text-gu-ink-muted">
              Arraste numa linha (ou carregue numa hora) para criar um turno. Arraste um turno para
              outra zona ou hora, ou puxe as pontas para mudar o início e o fim; com o teclado, as
              setas ← → mudam a hora, ↑ ↓ mudam a zona e Shift + ← → muda o fim. Ctrl+Z desfaz o
              último movimento. As tarefas do evento (◆) ficam a cargo de quem está de turno nessa
              zona a essa hora, ou de uma pessoa escolhida; a vermelho, não há ninguém. Só aparecem
              para escolher o staff e os voluntários e streamers confirmados nesse dia. Cada pessoa
              recebe um email quando é posta num turno, quando o turno muda no formulário e quando
              sai dele (arrastar não manda email), e vê os seus turnos em growup.gg/conta/eventos.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}

function DayHours({
  base,
  day,
  onSaved,
}: {
  base: string;
  day: ShiftDay;
  onSaved: (d: EventShifts) => void;
}) {
  const [open, setOpen] = useState(day.openHour);
  const [close, setClose] = useState(day.closeHour);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const changed = open !== day.openHour || close !== day.closeHour;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(
        eventShiftsSchema.parse(
          await send('PUT', `${base}/hours`, { day: day.day, openHour: open, closeHour: close }),
        ),
      );
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void save(e)}>
      <Field label="Abre às">
        <Select value={open} onChange={(e) => setOpen(Number(e.target.value))}>
          {range(0, 24).map((h) => (
            <option key={h} value={h}>
              {h}h
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Fecha às">
        <Select value={close} onChange={(e) => setClose(Number(e.target.value))}>
          {range(1, 25).map((h) => (
            <option key={h} value={h}>
              {h}h
            </option>
          ))}
        </Select>
      </Field>
      {changed ? (
        <Button type="submit" size="sm" variant="secondary" disabled={busy}>
          Guardar horas
        </Button>
      ) : (
        <span className="pb-2 text-caption text-gu-ink-muted">
          {day.custom ? 'Horas deste dia.' : 'Horas do início e do fim do evento.'}
        </span>
      )}
      {error ? <span className="pb-2 text-caption text-gu-danger">{error}</span> : null}
    </form>
  );
}

function Grid({
  data,
  day,
  onOpen,
  onOpenTask,
  onCreate,
  onMove,
}: {
  data: EventShifts;
  day: ShiftDay;
  onOpen: (shiftId: string) => void;
  onOpenTask: (taskId: string) => void;
  onCreate: (zoneId: string, startHour: number, endHour: number) => void;
  onMove: (shift: EventShift, to: Place) => void;
}) {
  const hours = range(day.openHour, day.closeHour);
  const span = hours.length;
  const [pick, setPick] = useState<{ zoneId: string; from: number; to: number } | null>(null);
  const finish = () => {
    if (pick) onCreate(pick.zoneId, Math.min(pick.from, pick.to), Math.max(pick.from, pick.to) + 1);
    setPick(null);
  };

  // O turno agarrado: onde se carregou, a largura de uma hora e onde vai ficar.
  const drag = useRef<{
    shift: EventShift;
    mode: DragMode;
    x: number;
    y: number;
    hourWidth: number;
    to: Place;
    moved: boolean;
  } | null>(null);
  const [preview, setPreview] = useState<(Place & { id: string }) | null>(null);
  // Largar um turno arrastado nao o abre.
  const dropped = useRef(false);
  // O turno movido com as setas volta a ter o foco, mesmo noutra zona.
  const focusId = useRef<string | null>(null);

  const begin = (shift: EventShift, mode: DragMode, e: ReactPointerEvent<HTMLElement>) => {
    dropped.current = false;
    if (e.button !== 0) return;
    const row = e.currentTarget.closest<HTMLElement>('[data-zone-row]');
    drag.current = {
      shift,
      mode,
      x: e.clientX,
      y: e.clientY,
      hourWidth: (row?.getBoundingClientRect().width ?? 0) / span,
      to: { zoneId: shift.zoneId, startHour: shift.startHour, endHour: shift.endHour },
      moved: false,
    };
  };

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      if (!d.moved && Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) < 5) return;
      d.moved = true;
      const delta = d.hourWidth > 0 ? Math.round((e.clientX - d.x) / d.hourWidth) : 0;
      let zoneId = d.to.zoneId;
      if (d.mode === 'move') {
        const row = document
          .elementFromPoint(e.clientX, e.clientY)
          ?.closest<HTMLElement>('[data-zone-row]');
        zoneId = row?.dataset.zoneRow ?? zoneId;
      }
      d.to = placeAfter(d.shift, d.mode, delta, zoneId, day.openHour, day.closeHour);
      setPreview({ id: d.shift.id, ...d.to });
    };
    const onPointerUp = () => {
      const d = drag.current;
      drag.current = null;
      if (!d?.moved) return;
      dropped.current = true;
      setPreview(null);
      onMove(d.shift, d.to);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [day.openHour, day.closeHour, onMove]);

  useEffect(() => {
    const id = focusId.current;
    if (id === null) return;
    const block = document.querySelector<HTMLElement>(`[data-shift-id="${id}"]`);
    if (block && block !== document.activeElement) block.focus();
    focusId.current = null;
  });

  const onKey = (shift: EventShift, e: KeyboardEvent<HTMLElement>) => {
    const zones = data.zones.map((z) => z.id);
    const at = zones.indexOf(shift.zoneId);
    let to: Place | null = null;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const delta = e.key === 'ArrowLeft' ? -1 : 1;
      to = placeAfter(
        shift,
        e.shiftKey ? 'end' : 'move',
        delta,
        shift.zoneId,
        day.openHour,
        day.closeHour,
      );
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const zoneId = zones[at + (e.key === 'ArrowUp' ? -1 : 1)];
      if (zoneId !== undefined) to = { ...shift, zoneId };
    }
    if (to === null) return;
    e.preventDefault();
    focusId.current = shift.id;
    onMove(shift, {
      zoneId: to.zoneId,
      startHour: to.startHour,
      endHour: to.endHour,
    });
  };

  const placed = data.shifts.map((s) =>
    preview?.id === s.id
      ? { ...s, zoneId: preview.zoneId, startHour: preview.startHour, endHour: preview.endHour }
      : s,
  );

  return (
    <div className="overflow-x-auto" onPointerUp={finish} onPointerLeave={() => setPick(null)}>
      <div
        className="grid min-w-[48rem] select-none"
        style={{ gridTemplateColumns: `12rem repeat(${String(span)}, minmax(3rem, 1fr))` }}
      >
        <div className="border-b border-gu-line p-2 font-sub text-label font-bold uppercase text-gu-ink-muted">
          Zona
        </div>
        {hours.map((h) => (
          <div
            key={h}
            className="border-b border-l border-gu-line p-2 font-sub text-label font-bold text-gu-ink-muted"
          >
            {h}h
          </div>
        ))}
        {data.zones.map((zone) => {
          const shifts = placed.filter((s) => s.day === day.day && s.zoneId === zone.id);
          const lane = lanes(shifts);
          const rows = Math.max(1, ...[...lane.values()].map((l) => l + 1));
          const tasks = data.tasks.filter((t) => t.day === day.day && t.zoneId === zone.id);
          const strip = tasks.length > 0 ? 1.75 : 0;
          return (
            <div key={zone.id} className="contents">
              <div className="flex items-center gap-2 border-b border-gu-line p-2 font-semibold">
                <span
                  aria-hidden="true"
                  className="size-3 shrink-0 border-2"
                  style={{ borderColor: ZONE_COLORS[zone.color].hex }}
                />
                {zone.name}
              </div>
              <div
                data-zone-row={zone.id}
                className="relative border-b border-gu-line"
                style={{
                  gridColumn: `span ${String(span)}`,
                  height: `${String(rows * 4.25 + strip)}rem`,
                }}
              >
                <div className="absolute inset-0 flex">
                  {hours.map((h) => (
                    <button
                      key={h}
                      type="button"
                      aria-label={`Novo turno em ${zone.name} às ${String(h)}h`}
                      className={cx(
                        'flex-1 border-l border-gu-line hover:bg-gu-raised',
                        pick?.zoneId === zone.id &&
                          h >= Math.min(pick.from, pick.to) &&
                          h <= Math.max(pick.from, pick.to) &&
                          'bg-gu-raised',
                      )}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        setPick({ zoneId: zone.id, from: h, to: h });
                      }}
                      onPointerEnter={() =>
                        setPick((p) => (p && p.zoneId === zone.id ? { ...p, to: h } : p))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onCreate(zone.id, h, h + 1);
                        }
                      }}
                    />
                  ))}
                </div>
                {shifts.map((s) => (
                  <ShiftBlock
                    key={s.id}
                    shift={s}
                    color={ZONE_COLORS[zone.color].hex}
                    left={((s.startHour - day.openHour) / span) * 100}
                    width={((s.endHour - s.startHour) / span) * 100}
                    lane={lane.get(s.id) ?? 0}
                    dragging={preview?.id === s.id}
                    onOpen={() => {
                      if (dropped.current) dropped.current = false;
                      else onOpen(s.id);
                    }}
                    onBegin={(mode, e) => {
                      const original = data.shifts.find((x) => x.id === s.id);
                      if (original) begin(original, mode, e);
                    }}
                    onKey={(e) => {
                      const original = data.shifts.find((x) => x.id === s.id);
                      if (original) onKey(original, e);
                    }}
                  />
                ))}
                {tasks.map((t) => (
                  <TaskMarker
                    key={t.id}
                    task={t}
                    left={((t.atMinute / 60 - day.openHour) / span) * 100}
                    top={rows * 4.25}
                    onOpen={() => onOpenTask(t.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ShiftBlock({
  shift,
  color,
  left,
  width,
  lane,
  dragging,
  onOpen,
  onBegin,
  onKey,
}: {
  shift: EventShift;
  color: string;
  left: number;
  width: number;
  lane: number;
  dragging: boolean;
  onOpen: () => void;
  onBegin: (mode: DragMode, e: ReactPointerEvent<HTMLElement>) => void;
  onKey: (e: KeyboardEvent<HTMLElement>) => void;
}) {
  const people = shift.people.map((p) => `${p.login}${p.overlap ? ' ⚠' : ''}`).join(', ');
  const edge = 'absolute inset-y-0 w-2 cursor-ew-resize hover:bg-gu-raised';
  return (
    <button
      type="button"
      data-shift-id={shift.id}
      onClick={onOpen}
      onPointerDown={(e) => {
        e.stopPropagation();
        const end = e.target instanceof HTMLElement ? e.target.dataset.edge : undefined;
        onBegin(end === 'start' || end === 'end' ? end : 'move', e);
      }}
      onKeyDown={onKey}
      aria-label={`Turno ${hoursLabel(shift.startHour, shift.endHour)}: ${people || 'por preencher'}`}
      aria-describedby="shift-keys"
      className={cx(
        'absolute flex touch-none flex-col overflow-hidden border-2 bg-gu-surface px-2 py-1 text-left',
        dragging ? 'z-10 cursor-grabbing shadow-lg' : 'cursor-grab',
      )}
      style={{
        left: `calc(${String(left)}% + 2px)`,
        width: `calc(${String(width)}% - 4px)`,
        top: `calc(${String(lane * 4.25)}rem + 4px)`,
        height: 'calc(4.25rem - 8px)',
        borderColor: shift.unfilled ? 'rgb(var(--gu-danger))' : color,
      }}
    >
      <span className="truncate text-caption font-semibold">
        {shift.people.length === 0 ? 'Por preencher' : people}
      </span>
      <span className="truncate text-[0.7rem] text-gu-ink-muted">
        {[
          shift.unfilled && shift.people.length > 0
            ? `por preencher: ${String(shift.people.length)} de ${String(shift.needed)}`
            : shift.people.length === 0 && shift.needed > 0
              ? `precisa de ${String(shift.needed)}`
              : null,
          hoursLabel(shift.startHour, shift.endHour),
          shift.note || null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>
      <span aria-hidden="true" data-edge="start" className={cx(edge, 'left-0')} />
      <span aria-hidden="true" data-edge="end" className={cx(edge, 'right-0')} />
    </button>
  );
}

/** Uma tarefa do evento na linha da zona, a hora certa (RF-E26). */
function TaskMarker({
  task,
  left,
  top,
  onOpen,
}: {
  task: EventTask;
  left: number;
  top: number;
  onOpen: () => void;
}) {
  const alert = task.uncovered && !task.done;
  return (
    <button
      type="button"
      onClick={onOpen}
      onPointerDown={(e) => e.stopPropagation()}
      aria-label={`Tarefa ${minuteLabel(task.atMinute)}: ${task.title} (${
        task.done ? 'feita' : taskOwner(task)
      })`}
      title={`${minuteLabel(task.atMinute)} ${task.title} · ${taskOwner(task)}`}
      className={cx(
        'absolute flex max-w-[10rem] items-center gap-1 truncate border bg-gu-surface px-1 text-[0.7rem]',
        alert ? 'border-gu-danger text-gu-danger' : 'border-gu-line',
        task.done && 'text-gu-ink-muted line-through',
      )}
      style={{ left: `${String(left)}%`, top: `calc(${String(top)}rem + 2px)` }}
    >
      <span aria-hidden="true">◆</span>
      {minuteLabel(task.atMinute)} {task.title}
    </button>
  );
}

/** Os turnos de cada pessoa em todos os dias (RF-E24). */
function PeopleView({ data }: { data: EventShifts }) {
  const rows = scheduleByPerson(data);
  if (rows.length === 0) return <p className="text-gu-ink-muted">Ainda ninguém tem turnos.</p>;
  return (
    <ul aria-label="Escala por pessoa" className="flex flex-col">
      {rows.map((r) => (
        <li
          key={r.accountId}
          className="grid gap-1 border-b border-gu-line py-2 sm:grid-cols-[14rem_1fr]"
        >
          <span className="font-semibold">
            {r.login}{' '}
            <span className="text-caption font-normal text-gu-ink-muted">
              {SHIFT_ROLE_LABELS[r.role]}
            </span>
          </span>
          <span className="flex flex-wrap gap-x-4 gap-y-1">
            {r.shifts.map((s) => (
              <span key={s.id} className={s.overlap ? 'text-gu-danger' : ''}>
                {dayLabel(s.day)} {hoursLabel(s.startHour, s.endHour)} {s.zone}
                {s.overlap ? ' ⚠' : ''}
              </span>
            ))}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function scheduleByPerson(data: EventShifts) {
  const zoneName = new Map(data.zones.map((z) => [z.id, z.name]));
  const people = new Map<
    string,
    {
      accountId: string;
      login: string;
      role: ShiftCandidate['role'];
      shifts: (EventShift & { zone: string; overlap: boolean })[];
    }
  >();
  for (const s of data.shifts) {
    for (const p of s.people) {
      const row = people.get(p.accountId) ?? {
        accountId: p.accountId,
        login: p.login,
        role: p.role,
        shifts: [],
      };
      row.shifts.push({ ...s, zone: zoneName.get(s.zoneId) ?? '—', overlap: p.overlap });
      people.set(p.accountId, row);
    }
  }
  return [...people.values()].sort((a, b) => a.login.localeCompare(b.login, 'pt'));
}

function ShiftEditor({
  base,
  data,
  shift,
  draft,
  onSaved,
  onClose,
}: {
  base: string;
  data: EventShifts;
  shift: EventShift | null;
  draft: { zoneId: string; day: string; startHour: number; endHour: number } | null;
  onSaved: (d: EventShifts) => void;
  onClose: () => void;
}) {
  const [zoneId, setZoneId] = useState(shift?.zoneId ?? draft?.zoneId ?? '');
  const [day, setDay] = useState(shift?.day ?? draft?.day ?? '');
  const [startHour, setStartHour] = useState(shift?.startHour ?? draft?.startHour ?? 9);
  const [endHour, setEndHour] = useState(shift?.endHour ?? draft?.endHour ?? 10);
  const [needed, setNeeded] = useState(String(shift?.needed ?? 1));
  const [note, setNote] = useState(shift?.note ?? '');
  const [people, setPeople] = useState<ShiftCandidate[]>(shift?.people ?? []);
  const [query, setQuery] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const dayInfo = data.days.find((d) => d.day === day) ?? data.days[0];
  const open = dayInfo?.openHour ?? 0;
  const close = dayInfo?.closeHour ?? 24;
  const overlapping = new Set(shift?.people.filter((p) => p.overlap).map((p) => p.accountId));

  const search = (q: string) =>
    getJson(
      `${base}/people?day=${encodeURIComponent(day)}&q=${encodeURIComponent(q)}`,
      z.array(shiftCandidateSchema),
    );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = Number(needed);
    if (!Number.isInteger(n) || n < 0 || n > 50) {
      setFields({ needed: 'Um número de 0 a 50.' });
      return;
    }
    if (startHour >= endHour) {
      setFields({ endHour: 'O fim tem de ser depois do início.' });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const body = {
        zoneId,
        day,
        startHour,
        endHour,
        needed: n,
        note,
        accountIds: people.map((p) => p.accountId),
      };
      const res = shift
        ? await send('PUT', `${base}/${shift.id}`, body)
        : await send('POST', base, body);
      onSaved(eventShiftsSchema.parse(res));
    } catch (err) {
      if (err instanceof ApiError) setFields(err.fields);
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!shift || !window.confirm('Apagar este turno? Quem lá está recebe um email.')) return;
    setBusy(true);
    try {
      onSaved(eventShiftsSchema.parse(await send('DELETE', `${base}/${shift.id}`)));
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={3} title={shift ? 'Editar turno' : 'Novo turno'}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Field label="Zona" error={fields.zoneId}>
            <Select value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
              {data.zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Dia" error={fields.day}>
            <Select
              value={day}
              onChange={(e) => {
                setDay(e.target.value);
                // Quem vem num dia pode nao vir no outro: escolhe-se outra vez.
                setPeople([]);
              }}
            >
              {data.days.map((d) => (
                <option key={d.day} value={d.day}>
                  {dayLabel(d.day)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Das" error={fields.startHour}>
            <Select value={startHour} onChange={(e) => setStartHour(Number(e.target.value))}>
              {range(open, close).map((h) => (
                <option key={h} value={h}>
                  {h}h
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Às" error={fields.endHour}>
            <Select value={endHour} onChange={(e) => setEndHour(Number(e.target.value))}>
              {range(open + 1, close + 1).map((h) => (
                <option key={h} value={h}>
                  {h}h
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
          <Field label="Pessoas precisas" error={fields.needed}>
            <Input inputMode="numeric" value={needed} onChange={(e) => setNeeded(e.target.value)} />
          </Field>
          <Field label="Nota" optional error={fields.note}>
            <Input
              value={note}
              maxLength={200}
              placeholder="abrir o check-in às 09:30"
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        </div>
        <Field
          label="Pessoas"
          optional
          hint="Staff, ou voluntários e streamers confirmados nesse dia."
          error={fields.accountIds}
        >
          <Autocomplete
            value={query}
            onChange={setQuery}
            search={search}
            getKey={(p) => p.accountId}
            renderItem={(p) => (
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-semibold">{p.login}</span>
                <span className="text-caption text-gu-ink-muted">{SHIFT_ROLE_LABELS[p.role]}</span>
              </span>
            )}
            onSelect={(p) => {
              setQuery('');
              setPeople((list) =>
                list.some((x) => x.accountId === p.accountId) ? list : [...list, p],
              );
            }}
            placeholder="Procurar pelo login…"
          />
        </Field>
        {people.length > 0 ? (
          <ul className="flex flex-wrap gap-2" aria-label="Pessoas no turno">
            {people.map((p) => (
              <li key={p.accountId} className="flex items-center gap-1 border border-gu-line px-2">
                {p.login}
                {overlapping.has(p.accountId) ? (
                  <span className="text-caption text-gu-danger">⚠ outro turno à mesma hora</span>
                ) : null}
                <button
                  type="button"
                  aria-label={`Tirar ${p.login}`}
                  className="text-gu-ink-muted hover:text-gu-ink"
                  onClick={() =>
                    setPeople((list) => list.filter((x) => x.accountId !== p.accountId))
                  }
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={busy}>
            Guardar
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          {shift ? (
            <Button variant="ghost" onClick={() => void remove()} disabled={busy}>
              Apagar
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}

const escape = (text: string) => text.replace(/[&<>"']/g, (c) => `&#${String(c.charCodeAt(0))};`);

function printWindow(title: string, body: string): void {
  const win = window.open('', '_blank');
  if (!win) return;
  win.document
    .write(`<!doctype html><html lang="pt"><head><meta charset="utf-8"><title>${escape(title)}</title>
<style>
@page { size: A4 landscape; margin: 10mm; }
body { font-family: system-ui, sans-serif; margin: 0; color: #111; font-size: 9pt; }
h1 { font-size: 15pt; margin: 0 0 4mm; text-transform: uppercase; }
table { border-collapse: collapse; width: 100%; table-layout: fixed; }
th, td { border: 1px solid #999; padding: 1.5mm; vertical-align: top; text-align: left; }
th.zone { width: 32mm; }
td.shift { border-width: 2px; }
* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
</style></head><body>${body}<script>window.onload = function () { window.print(); };</script></body></html>`);
  win.document.close();
}

/** A grelha de um dia numa folha A4 ao baixo, para afixar (RF-E24). */
export function printDay(title: string, data: EventShifts, day: ShiftDay): void {
  const hours = range(day.openHour, day.closeHour);
  const rows = data.zones
    .map((zone) => {
      const hex = ZONE_COLORS[zone.color].hex;
      const shifts = data.shifts
        .filter((s) => s.day === day.day && s.zoneId === zone.id)
        .sort((a, b) => a.startHour - b.startHour);
      let cells = '';
      let h = day.openHour;
      for (const s of shifts) {
        if (s.startHour < h) continue; // sobreposto na mesma zona: fica na escala por pessoa
        if (s.startHour > h) cells += '<td></td>'.repeat(s.startHour - h);
        const who = s.people.map((p) => escape(p.login)).join(', ') || 'Por preencher';
        cells += `<td class="shift" colspan="${String(s.endHour - s.startHour)}" style="border-color:${hex}"><b>${who}</b>${
          s.note ? `<br>${escape(s.note)}` : ''
        }</td>`;
        h = s.endHour;
      }
      if (h < day.closeHour) cells += '<td></td>'.repeat(day.closeHour - h);
      return `<tr><th class="zone" style="border-left:4px solid ${hex}">${escape(zone.name)}</th>${cells}</tr>`;
    })
    .join('');
  const zoneName = new Map(data.zones.map((z) => [z.id, z.name]));
  const tasks = data.tasks
    .filter((t) => t.day === day.day)
    .map(
      (t) =>
        `<tr><th class="zone">${minuteLabel(t.atMinute)}</th><td>${escape(t.title)}</td><td>${escape(
          zoneName.get(t.zoneId) ?? '—',
        )}</td><td>${escape(taskOwner(t))}</td></tr>`,
    )
    .join('');
  printWindow(
    `${title} · Turnos ${dayLabel(day.day)}`,
    `<h1>${escape(title)} · ${escape(dayLabel(day.day))} · ${hoursLabel(day.openHour, day.closeHour)}</h1>
<table><thead><tr><th class="zone">Zona</th>${hours.map((x) => `<th>${String(x)}h</th>`).join('')}</tr></thead><tbody>${rows}</tbody></table>${
      tasks ? `<h1 style="margin-top:6mm">Tarefas</h1><table><tbody>${tasks}</tbody></table>` : ''
    }`,
  );
}

/** A escala de cada pessoa em todos os dias (RF-E24). */
export function printPeople(title: string, data: EventShifts): void {
  const rows = scheduleByPerson(data)
    .map(
      (r) =>
        `<tr><th class="zone">${escape(r.login)}</th><td>${r.shifts
          .map(
            (s) =>
              `${escape(dayLabel(s.day))} ${hoursLabel(s.startHour, s.endHour)} ${escape(s.zone)}${
                s.note ? ` (${escape(s.note)})` : ''
              }`,
          )
          .join('<br>')}</td></tr>`,
    )
    .join('');
  printWindow(
    `${title} · Escala`,
    `<h1>${escape(title)} · Escala por pessoa</h1><table><tbody>${rows}</tbody></table>`,
  );
}
